import React from 'react';
import PropTypes from 'prop-types';
import i18n, { aliases } from '../../i18n/i18n';
import { fetcher } from '@weezevent/weezjs-core';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { Loading } from '../../components';

const FetchContext = React.createContext();

export const useFetch = () => React.useContext(FetchContext);

const AuthenticationInner = ({ children }) => {
    const auth = useAuth();

    React.useEffect(() => {
        if (!auth.user) {
            // No User
            return;
        }
        const userLocale = auth.user.profile.locale || auth.user.profile.data?.locale || 'en';
        const accountsTokenLocale = userLocale.replace(/_/g, '-');
        const locale = aliases[accountsTokenLocale] ?? accountsTokenLocale;

        if (locale !== i18n.language) {
            i18n.changeLanguage(locale);
        }
    }, [auth]);

    if (auth.isLoading || auth.error) {
        return <Loading active />;
    }

    if (auth.isAuthenticated) {
        // Create a fetch alias that injects the token
        const fetch = (url, opts) => {
            opts = opts || {};
            const headers = (opts.headers = opts.headers || {});
            headers.Authorization = `${auth.user.token_type} ${auth.user.access_token}`;
            return window.fetch(url, opts);
        };

        // Write the global mutable state
        const tokenType = 'Bearer'; //Should be auth.user.token_type
        fetcher.setAuthorizationHeader(`${tokenType} ${auth.user.access_token}`);
        // Inject a fetch command in a context
        return <FetchContext.Provider value={fetch}>{children}</FetchContext.Provider>;
    }
    return <SigninRedirect />;
};

const SigninRedirect = () => {
    const auth = useAuth();
    React.useEffect(() => {
        auth.signinRedirect({ state: { returnUrl: window.location.pathname + window.location.search } });
    }, []);
    return null;
};

export const Authentication = ({ children, redirectPath, config }) => {
    const onSigninCallback = user => {
        window.history.replaceState({}, document.title, user?.state?.returnUrl || window.location.pathname);
    };

    return (
        <AuthProvider
            automaticSilentRenew
            authority={config.OIDCUrl}
            redirect_uri={String(window.location.origin) + '/'}
            onSigninCallback={onSigninCallback}
            client_id={config.clientId}
        >
            <AuthenticationInner>{children}</AuthenticationInner>
        </AuthProvider>
    );
};

Authentication.propTypes = {
    children: PropTypes.node.isRequired,
    redirectPath: PropTypes.string.isRequired,
    config: PropTypes.shape({
        OIDCUrl: PropTypes.string,
        clientId: PropTypes.string
    }).isRequired
};
